import React from 'react';
import iconData from './icons.json';

export interface IconProps {
  /**
   * Name of the icon to display
   */
  name: string;

  /**
   * Size of the icon in pixels
   */
  size?: number;

  /**
   * Color of the icon
   */
  color?: string;

  /**
   * Optional CSS class name
   */
  className?: string;

  /**
   * Optional style overrides
   */
  style?: React.CSSProperties;

  /**
   * Optional click handler
   */
  onClick?: React.MouseEventHandler<SVGSVGElement>;

  /**
   * Aria label for accessibility
   */
  ariaLabel?: string;
}

export interface IconData {
  name: string;
  path: string;
  viewBox: string;
  fill?: string;
}

// Export icon data for use in other components
export const icons: IconData[] = iconData.icons;

// Create a map of icon names to icon data for quick lookup
const iconMap = new Map<string, IconData>();
icons.forEach((icon) => {
  iconMap.set(icon.name, icon);
});

export const Icon: React.FC<IconProps> = ({
  name,
  size = 24,
  color = 'currentColor',
  className = '',
  style = {},
  onClick,
  ariaLabel,
}) => {
  // Find the icon data for the given name
  const iconInfo = iconMap.get(name);

  // If the icon doesn't exist, return null or a fallback
  if (!iconInfo) {
    console.warn(`Icon "${name}" not found`);
    return null;
  }

  // Split the path string into multiple paths if it contains multiple commands
  const paths = iconInfo.path.split(' M ').map((path, index) => 
    index === 0 ? path : `M ${path}`
  );

  // Combine the style props
  const combinedStyle: React.CSSProperties = {
    display: 'inline-block',
    verticalAlign: 'middle',
    ...style,
  };

  return (
    <svg
      width={size}
      height={size}
      viewBox={iconInfo.viewBox}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={combinedStyle}
      onClick={onClick}
      aria-label={ariaLabel || `${name} icon`}
      role="img"
    >
      {paths.map((path, index) => (
        <path
          key={index}
          d={path}
          stroke={iconInfo.fill ? 'none' : color}
          fill={iconInfo.fill ? color : 'none'}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
    </svg>
  );
};
